import { create } from "zustand";
import { identifyAnalyticsUser, resetAnalyticsUser } from "@/lib/analytics";
import type { User } from "@/types/api";
import { logout as apiLogout, getMe, updateMe } from "@/api/auth";
import {
  scheduleProactiveRefresh,
  cancelProactiveRefresh,
} from "@/api/client";
import { setSentryUser, clearSentryUser } from "@/lib/sentry";
import { resetQueryCache } from "@/lib/queryClient";
import i18n from "@/i18n";

/**
 * Não há `login` aqui, e a ausência é o desenho.
 *
 * O login por senha foi removido: quem entra vai para
 * `/api/auth/entra/login` numa navegação de página inteira, o callback do Entra
 * põe os cookies e devolve o browser para a aplicação. Quando a página remonta,
 * quem descobre a sessão é o `loadUser` abaixo, chamado pelos guards. Não
 * sobrou nenhum caminho em que o front autentique alguém por conta própria.
 *
 * O que morreu junto: o `resetQueryCache()` que o `login` fazia antes da
 * chamada (o navegador recarrega, então o cache do módulo não sobrevive) e toda
 * a telemetria de `login_failed` — o Entra tem a dele, e as falhas voltam pela
 * tela como `?sso_error=`.
 */
interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  logout: () => Promise<void>;
  loadUser: () => Promise<void>;
  setLanguage: (language: string) => Promise<void>;
}

function applyUserLanguage(user: User) {
  const lang = user.language || "en-US";
  if (i18n.language !== lang) {
    i18n.changeLanguage(lang);
  }
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true,

  logout: async () => {
    cancelProactiveRefresh();
    try {
      await apiLogout();
    } catch {
      // Ignore network errors during logout — cookies may already be cleared
    }
    clearSentryUser();
    resetAnalyticsUser();
    // O `user` do store zerar não basta: o cache do React Query vive num módulo
    // e sobrevive à navegação inteira. Sem isto, a próxima conta a entrar vê os
    // projetos desta enquanto o `staleTime` de 30s não vence — e como o dado é
    // considerado fresco, nem refetch acontece. Só F5 limpava.
    resetQueryCache();
    set({ user: null, isAuthenticated: false });
    authChannel?.postMessage("logout");
  },

  loadUser: async () => {
    // Só sonda a sessão — chamada por AuthGuard e GuestGuard, que já decidem
    // sozinhos o que fazer com um 401 (redirecionar ou mostrar a rota
    // pública). skipAuthRedirect evita que o interceptor global tome a
    // decisão por eles com um reload duro (SIMP.2 #423).
    set({ isLoading: true });
    try {
      const user = await getMe({ skipAuthRedirect: true });
      applyUserLanguage(user);
      scheduleProactiveRefresh();
      setSentryUser({ id: user.id, email: user.email, tenant_id: user.tenant_id });
      identifyAnalyticsUser(user);
      set({ user, isAuthenticated: true, isLoading: false });
    } catch {
      clearSentryUser();
      resetAnalyticsUser();
      set({ user: null, isAuthenticated: false, isLoading: false });
    }
  },

  setLanguage: async (language: string) => {
    const updated = await updateMe({ language });
    applyUserLanguage(updated);
    set((state) => ({
      user: state.user ? { ...state.user, language: updated.language } : state.user,
    }));
  },
}));

// Cross-tab logout synchronization via BroadcastChannel
let authChannel: BroadcastChannel | null = null;

if (typeof window !== "undefined" && typeof BroadcastChannel !== "undefined") {
  authChannel = new BroadcastChannel("mygrow_auth");
  authChannel.onmessage = (event) => {
    if (event.data === "logout") {
      cancelProactiveRefresh();
      clearSentryUser();
      resetAnalyticsUser();
      // Esta aba não passou pelo `logout()` — quem saiu foi a outra. Sem limpar
      // aqui também, ela fica com a tela montada sobre os dados de uma sessão
      // que já não existe.
      resetQueryCache();
      useAuthStore.setState({ user: null, isAuthenticated: false });
    }
  };
}
